import { useCallback, useMemo,useState } from 'react';
import { useAppDispatch,useAppSelector } from '@store/hooks';

import {
  type AppointmentType,
  type CreateAppointmentTypeRequest,
  type UpdateAppointmentTypeRequest,
} from '../types';
import { getApiErrorMessage } from '../utils/permissions';

import {
  useCreateEncounterTypeMutation,
  useCreateOrganizationEncounterTypeMutation,
  useDeleteEncounterTypeMutation,
  useDeleteOrganizationEncounterTypeMutation,
  useGetEncounterTypesQuery,
  useGetOrganizationEncounterTypesQuery,
  useUpdateEncounterTypeMutation,
  useUpdateOrganizationEncounterTypeMutation,
} from './api';
import { makeSelectFieldExpanded } from './selectors';
import { setExpandableFieldState,toggleExpandableField } from './slice';

export interface DeleteAppointmentTypeResult {
  success: boolean;
  /** API error message when the delete failed (e.g. permission denied). */
  message?: string;
}

/**
 * Hook for managing appointment type selection and search
 */
export function useAppointmentTypeSelection() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const selectAppointmentType = useCallback((id: string | null) => {
    setSelectedId(id);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedId(null);
  }, []);

  const updateSearchTerm = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  return {
    selectedId,
    searchTerm,
    selectAppointmentType,
    clearSelection,
    updateSearchTerm,
  };
}

interface UseAppointmentTypesOptions {
  skip?: boolean;
}

/**
 * Hook for fetching and filtering appointment types
 */
export function useAppointmentTypes(
  searchTerm?: string,
  providerId?: string,
  options: UseAppointmentTypesOptions = {},
) {
  // Fetch all appointment types (optionally for a specific provider)
  const {
    data: appointmentTypesResponse,
    isLoading,
    error,
    refetch,
  } = useGetEncounterTypesQuery({ perPage: 100, providerId }, { skip: options.skip });

  // Client-side filtering (until server-side search is implemented)
  const filteredAppointmentTypes = useMemo(() => {
    if (!appointmentTypesResponse?.items) return [];

    const appointmentTypes = appointmentTypesResponse.items as AppointmentType[];

    if (!searchTerm || searchTerm.trim() === '') {
      return appointmentTypes;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return appointmentTypes.filter(appointmentType =>
      appointmentType.encounter_type.toLowerCase().includes(lowerSearchTerm),
    );
  }, [appointmentTypesResponse?.items, searchTerm]);

  return {
    appointmentTypes: filteredAppointmentTypes,
    totalCount: appointmentTypesResponse?.total || 0,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook for fetching organization-level appointment types.
 */
export function useOrganizationAppointmentTypes(
  organizationId?: string | null,
  searchTerm?: string,
  options: UseAppointmentTypesOptions = {},
) {
  const {
    data: appointmentTypesResponse,
    isLoading,
    error,
    refetch,
  } = useGetOrganizationEncounterTypesQuery(
    { orgId: organizationId || '' },
    { skip: options.skip || !organizationId },
  );

  const filteredAppointmentTypes = useMemo(() => {
    if (!appointmentTypesResponse?.items) return [];

    const appointmentTypes = appointmentTypesResponse.items as AppointmentType[];

    if (!searchTerm || searchTerm.trim() === '') {
      return appointmentTypes;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return appointmentTypes.filter(appointmentType =>
      appointmentType.encounter_type.toLowerCase().includes(lowerSearchTerm),
    );
  }, [appointmentTypesResponse?.items, searchTerm]);

  return {
    appointmentTypes: filteredAppointmentTypes,
    totalCount: appointmentTypesResponse?.total || 0,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook for creating new appointment types
 */
export function useCreateAppointmentType() {
  const [createEncounterType, { isLoading: isCreating, error: createError }] =
    useCreateEncounterTypeMutation();

  const createNewAppointmentType = useCallback(
    async (appointmentTypeData: CreateAppointmentTypeRequest): Promise<AppointmentType> => {
      const result = await createEncounterType({
        encounterTypeCreate: appointmentTypeData,
      }).unwrap();
      return result;
    },
    [createEncounterType],
  );

  return {
    createNewAppointmentType,
    isCreating,
    createError,
  };
}

/**
 * Hook for creating organization-level appointment types.
 */
export function useCreateOrganizationAppointmentType(organizationId?: string | null) {
  const [createEncounterType, { isLoading: isCreating, error: createError }] =
    useCreateOrganizationEncounterTypeMutation();

  const createNewAppointmentType = useCallback(
    async (appointmentTypeData: CreateAppointmentTypeRequest): Promise<AppointmentType> => {
      if (!organizationId) {
        throw new Error('Organization ID is required to create appointment types');
      }

      const result = await createEncounterType({
        orgId: organizationId,
        encounterTypeCreate: appointmentTypeData,
      }).unwrap();
      return result;
    },
    [createEncounterType, organizationId],
  );

  return {
    createNewAppointmentType,
    isCreating,
    createError,
  };
}

/**
 * Hook for updating appointment types
 */
export function useUpdateAppointmentType() {
  const [updateEncounterType, { isLoading: isUpdating, error: updateError }] =
    useUpdateEncounterTypeMutation();

  const updateAppointmentType = useCallback(
    async (
      encounterTypeId: string,
      updateData: UpdateAppointmentTypeRequest,
    ): Promise<AppointmentType> => {
      const result = await updateEncounterType({
        encounterTypeId,
        encounterTypeUpdate: updateData,
      }).unwrap();
      return result;
    },
    [updateEncounterType],
  );

  return {
    updateAppointmentType,
    isUpdating,
    updateError,
  };
}

/**
 * Hook for updating organization-level appointment types.
 */
export function useUpdateOrganizationAppointmentType(organizationId?: string | null) {
  const [updateEncounterType, { isLoading: isUpdating, error: updateError }] =
    useUpdateOrganizationEncounterTypeMutation();

  const updateAppointmentType = useCallback(
    async (
      encounterTypeId: string,
      updateData: UpdateAppointmentTypeRequest,
    ): Promise<AppointmentType> => {
      if (!organizationId) {
        throw new Error('Organization ID is required to update appointment types');
      }

      const result = await updateEncounterType({
        orgId: organizationId,
        encounterTypeId,
        encounterTypeUpdate: updateData,
      }).unwrap();
      return result;
    },
    [updateEncounterType, organizationId],
  );

  return {
    updateAppointmentType,
    isUpdating,
    updateError,
  };
}

/**
 * Hook for deleting appointment types
 */
export function useDeleteAppointmentType() {
  const [deleteEncounterType, { isLoading: isDeleting, error: deleteError }] =
    useDeleteEncounterTypeMutation();

  const deleteAppointmentType = useCallback(
    async (encounterTypeId: string): Promise<DeleteAppointmentTypeResult> => {
      try {
        await deleteEncounterType({ encounterTypeId }).unwrap();
        return { success: true };
      } catch (error) {
        console.error('Failed to delete appointment type:', error);
        return { success: false, message: getApiErrorMessage(error) };
      }
    },
    [deleteEncounterType],
  );

  return {
    deleteAppointmentType,
    isDeleting,
    deleteError,
  };
}

/**
 * Hook for deleting organization-level appointment types.
 */
export function useDeleteOrganizationAppointmentType(organizationId?: string | null) {
  const [deleteEncounterType, { isLoading: isDeleting, error: deleteError }] =
    useDeleteOrganizationEncounterTypeMutation();

  const deleteAppointmentType = useCallback(
    async (encounterTypeId: string): Promise<DeleteAppointmentTypeResult> => {
      if (!organizationId) {
        throw new Error('Organization ID is required to delete appointment types');
      }

      try {
        await deleteEncounterType({ orgId: organizationId, encounterTypeId }).unwrap();
        return { success: true };
      } catch (error) {
        console.error('Failed to delete organization appointment type:', error);
        return { success: false, message: getApiErrorMessage(error) };
      }
    },
    [deleteEncounterType, organizationId],
  );

  return {
    deleteAppointmentType,
    isDeleting,
    deleteError,
  };
}

/**
 * Hook for managing expandable field state
 */
export function useExpandableField(fieldId: string) {
  const dispatch = useAppDispatch();
  const expanded = useAppSelector(makeSelectFieldExpanded(fieldId));

  const toggle = useCallback(() => {
    dispatch(toggleExpandableField(fieldId));
  }, [dispatch, fieldId]);

  const setExpanded = useCallback(
    (expanded: boolean) => {
      dispatch(setExpandableFieldState({ fieldId, expanded }));
    },
    [dispatch, fieldId],
  );

  return {
    expanded,
    toggle,
    setExpanded,
  };
}

/**
 * Main hook that combines all appointment type functionality
 */
export function useAppointmentTypeManagement() {
  const selection = useAppointmentTypeSelection();
  const appointmentTypesQuery = useAppointmentTypes(selection.searchTerm);
  const creation = useCreateAppointmentType();

  return {
    // Selection state
    ...selection,

    // Data fetching
    ...appointmentTypesQuery,

    // Creation
    ...creation,
  };
}
